/**
 * F-022 — Settings persistence.
 *
 * Owns user preferences (volume · sensitivity · accessibility flags
 * arriving with F-026). Subscribes broadcast on any field change.
 * LocalStorage-backed via the same KVStorage interface used by F-020.
 */

import { SUPPORTED_LOCALES, type Locale } from "../i18n/index.ts";
import {
  createMemoryStorage,
  type KVStorage,
} from "./high-score-store.ts";

const STORAGE_KEY = "cosmic-suika.settings.v1";

export interface Settings {
  volume: number; // 0..1
  sensitivity: number; // 0.25..2.0 (slingshot power multiplier)
  muted: boolean;
  /** Optional manual override — when set, overrides browser auto-detect. */
  locale?: Locale;
}

export const DEFAULT_SETTINGS: Readonly<Settings> = {
  volume: 0.7,
  sensitivity: 1.0,
  muted: false,
};

export interface SettingsStore {
  current(): Readonly<Settings>;
  patch(partial: Partial<Settings>): Settings;
  reset(): Settings;
  subscribe(listener: (settings: Readonly<Settings>) => void): () => void;
}

export interface SettingsStoreOptions {
  storage?: KVStorage;
  storageKey?: string;
}

function clampVolume(v: number): number {
  return Math.max(0, Math.min(1, v));
}

function clampSensitivity(v: number): number {
  return Math.max(0.25, Math.min(2.0, v));
}

function normalizeLocale(value: unknown): Locale | undefined {
  if (typeof value !== "string") return undefined;
  return (SUPPORTED_LOCALES as ReadonlyArray<string>).includes(value)
    ? (value as Locale)
    : undefined;
}

function normalize(input: Partial<Settings>): Settings {
  const out: Settings = {
    volume: clampVolume(input.volume ?? DEFAULT_SETTINGS.volume),
    sensitivity: clampSensitivity(
      input.sensitivity ?? DEFAULT_SETTINGS.sensitivity,
    ),
    muted: Boolean(input.muted ?? DEFAULT_SETTINGS.muted),
  };
  const locale = normalizeLocale(input.locale);
  if (locale) out.locale = locale;
  return out;
}

function defaultStorage(): KVStorage {
  if (typeof window === "undefined") return createMemoryStorage();
  try {
    return window.localStorage;
  } catch {
    return createMemoryStorage();
  }
}

export function createSettingsStore(
  options: SettingsStoreOptions = {},
): SettingsStore {
  const storage = options.storage ?? defaultStorage();
  const key = options.storageKey ?? STORAGE_KEY;
  const listeners = new Set<(s: Readonly<Settings>) => void>();

  let state: Settings = (() => {
    const raw = storage.getItem(key);
    if (!raw) return { ...DEFAULT_SETTINGS };
    try {
      return normalize(JSON.parse(raw) as Partial<Settings>);
    } catch {
      return { ...DEFAULT_SETTINGS };
    }
  })();

  const persist = (): void => {
    try {
      storage.setItem(key, JSON.stringify(state));
    } catch {
      /* quota — silent */
    }
  };

  const announce = (): void => {
    const snap = current();
    for (const l of listeners) l(snap);
  };

  const current = (): Readonly<Settings> => ({ ...state });

  const patch = (partial: Partial<Settings>): Settings => {
    state = normalize({ ...state, ...partial });
    persist();
    announce();
    return current();
  };

  const reset = (): Settings => {
    state = { ...DEFAULT_SETTINGS };
    persist();
    announce();
    return current();
  };

  const subscribe = (listener: (s: Readonly<Settings>) => void): (() => void) => {
    listeners.add(listener);
    listener(current());
    return () => listeners.delete(listener);
  };

  return { current, patch, reset, subscribe };
}

export const __testing = { STORAGE_KEY };
